import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  DialogTitle
} from '@mui/material';
import {
  Phone as PhoneIcon,
  PlayArrow as PlayIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import AudioPlayer from '../AudioPlayer';

const RecentCallsTable = ({ data }) => {
  // Handle case where data might be the entire response object
  const processedData = Array.isArray(data) ? data : (data?.data || []);
  const [audioDialogOpen, setAudioDialogOpen] = useState(false);
  const [selectedRecording, setSelectedRecording] = useState(null);
  
  if (!processedData || processedData.length === 0) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        height={200}
        flexDirection="column"
      >
        <Typography variant="body2" color="text.secondary" gutterBottom>
          No recent calls available
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Recent calls will appear here once you have call data
        </Typography>
      </Box>
    );
  }

  const formatDuration = (seconds) => {
    if (!seconds) return '0m';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
      case 'cancelled':
        return 'error';
      case 'ringing':
      case 'answered':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      case 'cancelled':
        return 'Cancelled';
      case 'ringing':
        return 'Ringing';
      case 'answered':
        return 'Answered';
      case 'initiated':
        return 'Initiated';
      default:
        return status || 'Unknown';
    }
  };

  const handlePlayRecording = (recordingUrl) => {
    if (recordingUrl) {
      setSelectedRecording(recordingUrl);
      setAudioDialogOpen(true);
    }
  };

  const handleDownloadRecording = (recordingUrl) => {
    if (recordingUrl) {
      const link = document.createElement('a');
      link.href = recordingUrl;
      link.download = `recording-${Date.now()}.mp3`;
      link.click();
    }
  };

  const handleViewDetails = (callId) => {
    // Navigate to call details page
    window.location.href = `/calls/${callId}`;
  };

  const handleCloseAudioDialog = () => {
    setAudioDialogOpen(false);
    setSelectedRecording(null);
  };

  return (
    <>
      <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Agent</TableCell>
            <TableCell>Phone Number</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Duration</TableCell>
            <TableCell align="right">Time</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {processedData.map((call, index) => (
            <TableRow key={call.id || index} hover>
              <TableCell>
                <Box display="flex" alignItems="center" gap={1}>
                  <Avatar 
                    sx={{ 
                      width: 32, 
                      height: 32,
                      bgcolor: 'primary.light'
                    }}
                  >
                    <PhoneIcon sx={{ fontSize: 16 }} />
                  </Avatar>
                  <Typography variant="body2" fontWeight="medium">
                    {call.agentName || 'Unknown Agent'}
                  </Typography>
                </Box>
              </TableCell>
              
              <TableCell>
                <Typography variant="body2">
                  {call.phoneNumber || 'Unknown'}
                </Typography>
              </TableCell>
              
              <TableCell>
                <Chip
                  label={getStatusLabel(call.status)}
                  size="small"
                  color={getStatusColor(call.status)}
                  variant="outlined"
                />
              </TableCell>
              
              <TableCell align="right">
                <Typography variant="body2">
                  {formatDuration(call.duration)}
                </Typography>
              </TableCell>
              
              <TableCell align="right">
                <Typography variant="body2">
                  {formatDate(call.startTime)}
                </Typography>
              </TableCell>
              
              <TableCell align="center">
                <Box display="flex" justifyContent="center" gap={0.5}>
                  <Tooltip title="View Details">
                    <IconButton 
                      size="small" 
                      onClick={() => handleViewDetails(call.id)}
                    >
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  
                  {call.recordingUrl && (
                    <>
                      <Tooltip title="Play Recording">
                        <IconButton 
                          size="small" 
                          onClick={() => handlePlayRecording(call.recordingUrl)}
                        >
                          <PlayIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Download Recording">
                        <IconButton 
                          size="small" 
                          onClick={() => handleDownloadRecording(call.recordingUrl)}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>

    {/* Audio Player Dialog */}
    <Dialog
      open={audioDialogOpen}
      onClose={handleCloseAudioDialog}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Call Recording</Typography>
          <IconButton onClick={handleCloseAudioDialog}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {selectedRecording && (
          <AudioPlayer
            audioUrl={selectedRecording}
            title="Call Recording"
            height={80}
            showTitle={false}
          />
        )}
      </DialogContent>
    </Dialog>
    </>
  );
};

export default RecentCallsTable; 